import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { KmlService, type UploadedKml } from '../../services/kml/kmlService';
import ModalConfirmarEliminar from '../modales/ModalConfirmarEliminar';
import { useTranslation } from 'react-i18next';

// ===============================
// INTERFACES
// ===============================

interface KmlListProps {
  archivos: UploadedKml[];
  onEliminar: (archivo: UploadedKml, index: number) => void;
  readonly?: boolean;
}

// ===============================
// COMPONENTE LISTA KML
// ===============================

export const KmlList: React.FC<KmlListProps> = ({ 
  archivos, 
  onEliminar,
  readonly = false 
}) => {
  
  // Estado para modal de confirmación
  const { t } = useTranslation();
  const [modalEliminarOpen, setModalEliminarOpen] = useState(false);
  const [archivoAEliminar, setArchivoAEliminar] = useState<{archivo: UploadedKml, index: number} | null>(null);
  
  /**
   * Abrir modal para eliminar archivo
   */
  const abrirModalEliminar = (archivo: UploadedKml, index: number) => {
    if (readonly) return;
    setArchivoAEliminar({ archivo, index });
    setModalEliminarOpen(true);
  };

  /**
   * Confirmar eliminación
   */
  const confirmarEliminar = async () => {
    if (!archivoAEliminar) return;

    try {
      // En desarrollo, eliminar directamente
      // En checkpoint 5 se usará: await KmlService.delete(archivoAEliminar.archivo.archivo_id);
      onEliminar(archivoAEliminar.archivo, archivoAEliminar.index);
      toast.success(`Archivo "${archivoAEliminar.archivo.nombre_original}" eliminado`);
      setModalEliminarOpen(false);
      setArchivoAEliminar(null);
    } catch (error) {
      console.error('Error eliminando archivo KML:', error);
      toast.error('Error al eliminar archivo KML');
    }
  };

  /**
   * Cancelar eliminación
   */
  const cancelarEliminar = () => {
    setModalEliminarOpen(false);
    setArchivoAEliminar(null);
  };

  /**
   * Obtener icono de estado
   */
  const getEstadoIcon = (estado: string) => {
    switch (estado) {
      case 'pendiente':
        return '⏳';
      case 'procesando':
        return '🔄';
      case 'completado':
        return '✅';
      case 'error':
        return '❌';
      default:
        return '📄';
    }
  };

  /**
   * Obtener color de estado
   */
  const getEstadoColor = (estado: string) => {
    switch (estado) {
      case 'pendiente':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'procesando':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'completado':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'error':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  /**
   * Formatear nombre de archivo
   */
  const formatearNombre = (nombre: string, maxLength: number = 30) => {
    if (nombre.length <= maxLength) return nombre;
    
    const extension = nombre.substring(nombre.lastIndexOf('.'));
    const nombreSinExtension = nombre.substring(0, nombre.lastIndexOf('.'));
    const nombreRecortado = nombreSinExtension.substring(0, maxLength - extension.length - 3);
    
    return `${nombreRecortado}...${extension}`;
  };

  // Si no hay archivos, mostrar mensaje
  if (archivos.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
        <div className="text-gray-400 mb-2">
          <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <p className="text-sm text-gray-500">{t('kml.title')}</p>
        <p className="text-xs text-gray-400 mt-1">{t('kml.drag_drop')}</p>
      </div>
    );
  }

  // ✅ ACTUALIZADO: Calcular estadísticas incluyendo líneas
  const areaTotal = KmlService.calcularAreaTotal(archivos);
  const longitudTotal = archivos.reduce((total, archivo) => {
    return total + (archivo.longitud_metros || 0);
  }, 0);
  const totalPoligonos = archivos.reduce((total, archivo) => {
    return total + (archivo.cantidad_poligonos || 0);
  }, 0);
  const totalLineas = archivos.reduce((total, archivo) => {
    return total + (archivo.cantidad_lineas || 0);
  }, 0);
  const archivosCompletados = archivos.filter(a => a.estado === 'completado').length;
  const archivosProcesando = archivos.filter(a => a.estado === 'procesando').length;
  const archivosConError = archivos.filter(a => a.estado === 'error').length;

  return (
    <div className="space-y-4">
      
      {/* ✅ ACTUALIZADO: Estadísticas consolidadas con líneas */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 text-center">
          <div>
            <p className="text-sm font-medium text-blue-700">{t('kml.title')}</p>
            <p className="text-lg font-bold text-blue-900">{archivos.length}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-blue-700">{t('kml.kml_area')}</p>
            <p className="text-lg font-bold text-blue-900">
              {areaTotal > 0 ? `${areaTotal.toFixed(2)} ${t('kml.hectares')}` : t('common.no_data')}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-blue-700">{t('kml.kml_length')}</p>
            <p className="text-lg font-bold text-orange-600">
              {longitudTotal > 0 ? `${(longitudTotal/1000).toFixed(2)} ${t('kml.kilometers')}` : t('common.no_data')}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-blue-700">{t('common.active')}</p>
            <p className="text-lg font-bold text-green-600">{archivosCompletados}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-blue-700">{t('common.processing')}</p>
            <p className="text-lg font-bold text-yellow-600">{archivosProcesando}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-blue-700">{t('kml.polygons')}/{t('kml.lines')}</p>
            <p className="text-xs text-blue-600">
              {totalPoligonos > 0 && `🔺 ${totalPoligonos}`}
              {totalPoligonos > 0 && totalLineas > 0 && ' • '}
              {totalLineas > 0 && `📏 ${totalLineas}`}
              {totalPoligonos === 0 && totalLineas === 0 && t('common.no_data')}
            </p>
          </div>
        </div>
      </div>

      {/* Lista de archivos */}
      <div className="space-y-2">
        <h5 className="text-sm font-medium text-gray-700">{t('faenas.kml_files')}:</h5>
        
        {archivos.map((archivo, index) => (
          <div 
            key={`${archivo.archivo_id}-${index}`}
            className={`
              flex items-center justify-between p-3 border rounded-lg transition-all
              ${getEstadoColor(archivo.estado)}
            `}
          >
            
            {/* Información del archivo */}
            <div className="flex items-center space-x-3 flex-1 min-w-0">
              
              {/* Estado */}
              <div className="flex-shrink-0">
                <span className="text-lg">{getEstadoIcon(archivo.estado)}</span>
              </div>
              
              {/* Nombre y detalles */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate" title={archivo.nombre_original}>
                  {formatearNombre(archivo.nombre_original)}
                </p>
                
                <div className="flex items-center space-x-4 mt-1">
                  {/* Área - solo si hay polígonos y > 0 */}
                  {!!(archivo.area_hectareas !== undefined && archivo.area_hectareas !== null && archivo.area_hectareas > 0) && (
                    <span className="text-xs">
                      🏢️ {archivo.area_hectareas.toFixed(2)} ha
                    </span>
                  )}
                  
                  {/* ✅ NUEVO: Longitud - solo si hay líneas y > 0 */}
                  {!!(archivo.longitud_metros !== undefined && archivo.longitud_metros !== null && archivo.longitud_metros > 0) && (
                    <span className="text-xs">
                      📏 {(archivo.longitud_metros/1000).toFixed(2)} km
                    </span>
                  )}
                  
                  {/* Polígonos */}
                  {!!(archivo.cantidad_poligonos !== undefined && archivo.cantidad_poligonos > 0) && (
                    <span className="text-xs">
                      🔵 {archivo.cantidad_poligonos} polígono{archivo.cantidad_poligonos !== 1 ? 's' : ''}
                    </span>
                  )}
                  
                  {/* ✅ NUEVO: Líneas */}
                  {!!(archivo.cantidad_lineas !== undefined && archivo.cantidad_lineas > 0) && (
                    <span className="text-xs">
                      📏 {archivo.cantidad_lineas} línea{archivo.cantidad_lineas !== 1 ? 's' : ''}
                    </span>
                  )}
                  
                  {/* Estado text */}
                  <span className="text-xs font-medium">
                    {archivo.estado === 'pendiente' && t('common.status')}
                    {archivo.estado === 'procesando' && t('common.processing')}
                    {archivo.estado === 'completado' && t('common.active')}
                    {archivo.estado === 'error' && t('common.error')}
                  </span>
                </div>
                
                {/* Mensaje */}
                {archivo.mensaje && (
                  <p className="text-xs mt-1 opacity-75">
                    {archivo.mensaje}
                  </p>
                )}
              </div>
            </div>

            {/* Acciones */}
            <div className="flex items-center space-x-2 flex-shrink-0">
              
              {/* Información adicional */}
              {archivo.estado === 'procesando' && (
                <div className="text-xs animate-pulse">
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
              
              {/* Botón eliminar */}
              {!readonly && (
                <button
                  onClick={() => abrirModalEliminar(archivo, index)}
                  className="p-1 text-red-500 hover:text-red-700 hover:bg-red-100 rounded transition-colors"
                  title="Eliminar archivo KML"
                  disabled={archivo.estado === 'procesando'}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Información adicional */}
      {archivosConError > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="flex items-center space-x-2">
            <span className="text-red-500">⚠️</span>
            <p className="text-sm text-red-700">
              <strong>{archivosConError}</strong> archivo(s) tuvieron errores durante el procesamiento.
            </p>
          </div>
        </div>
      )}
      
      {/* Modal Confirmar Eliminar KML */}
      <ModalConfirmarEliminar
        isOpen={modalEliminarOpen}
        titulo={t('kml.delete_kml')}
        mensaje={`${t('reports.confirm_delete')} "${archivoAEliminar?.archivo.nombre_original}"?`}
        detalles={archivoAEliminar ? [
          `Tipo: Archivo ${archivoAEliminar.archivo.nombre_original.toLowerCase().endsWith('.kmz') ? 'KMZ' : 'KML'}`,
          // ✅ MOSTRAR: Área solo si tiene polígonos y > 0
          ...(archivoAEliminar.archivo.area_hectareas !== null && archivoAEliminar.archivo.area_hectareas !== undefined && archivoAEliminar.archivo.area_hectareas > 0
            ? [`Área calculada: ${archivoAEliminar.archivo.area_hectareas.toFixed(2)} hectáreas`] 
            : []),
          // ✅ MOSTRAR: Longitud solo si tiene líneas y > 0
          ...(archivoAEliminar.archivo.longitud_metros !== null && archivoAEliminar.archivo.longitud_metros !== undefined && archivoAEliminar.archivo.longitud_metros > 0
            ? [`Longitud calculada: ${(archivoAEliminar.archivo.longitud_metros/1000).toFixed(2)} kilómetros`]
            : []),
          // ✅ MOSTRAR: Conteo de geometrías
          ...(archivoAEliminar.archivo.cantidad_poligonos && archivoAEliminar.archivo.cantidad_poligonos > 0
            ? [`Polígonos: ${archivoAEliminar.archivo.cantidad_poligonos} polígono(s)`]
            : []),
          ...(archivoAEliminar.archivo.cantidad_lineas && archivoAEliminar.archivo.cantidad_lineas > 0
            ? [`Líneas: ${archivoAEliminar.archivo.cantidad_lineas} línea(s)`]
            : []),
          `${t('common.status')}: ${archivoAEliminar.archivo.estado}`,
          t('reports.delete_warning')
        ] : []}
        onConfirmar={confirmarEliminar}
        onCancelar={cancelarEliminar}
        confirmText={t('reports.yes_delete')}
        cancelText={t('common.cancel')}
        tipo="danger"
      />
    </div>
  );
};

export default KmlList;